import useLocalStorage from 'app/model/LocalStorage';
import React, { useState, useEffect, useCallback, useRef } from 'react';

export const WebViewManagerContext = React.createContext(undefined);

// listeners (webapp -> webview):
//      listen-udp: (port: int)
//      broadcast-udp: (address: str, port: int, data: str) | address: '<an ip>' or 'auto'
//      unicast-udp: (address: str, port: int, data: str)
//      start-esptouch-v1: (bssid: str, ssid: str, password: str, deviceCount: int, mode) | mode: 'broadcast' or 'multicast'
//      request-push-token: ()
//      set-url: (url: str)
//      set-inspectable: (enable: bool)
//      get-wifi-info: ()
//
// listeners (webview -> webapp):
//      got-udp: (address: str, port: int, data: str)
//      got-push-token: (provider: str, token: str)
//      got-wifi-info: (bssid: str, ssid: str)
//
// webViewState:
//      webViewLoaded
//      inWebView
//      supportUDP
//      supportESPTouchV1

const _addListener = function (listeners, key, callback) {
    console.log("WebViewManagerContext _addListener", key);
    if (listeners[key] === undefined)
        listeners[key] = [];
    listeners[key].push(callback);
};
const _removeListener = function (listeners, key, callback) {
    console.log("WebViewManagerContext _removeListener", key);
    if (listeners[key] !== undefined) {
        const index = listeners[key].indexOf(callback);
        if (index > -1) {
            listeners[key].splice(index, 1);
        }
    }
};
const _notify = function (listeners, key, ...data) {
    if (data) {
        console.log("WebViewManagerContext _notify", key, ...data);
    } else {
        console.log("WebViewManagerContext _notify", key);
    }
    if (listeners[key] !== undefined) {
        for (let i = 0; i < listeners[key].length; i++) {
            const callback = listeners[key][i];
            try {
                if (data) {
                    callback(...data);
                } else {
                    callback()
                }
            } catch (error) {
                console.error("error in webViewManager.notify:", key, error);
            }
        }
    } else {
        console.log("webViewManager.notify: no listeners for key", key);
    }
};

export function WebViewManagerContextProvider(props) {
    const [webViewState, setWebViewState] = useState((window.WebViewManagerDefaultState !== undefined) ? window.WebViewManagerDefaultState : {});
    // const [webViewState, setWebViewState] = useState({ // for test
    //     webViewLoaded: true,
    //     inWebView: true,
    //     supportUDP: true,
    //     supportESPTouchV1: true,
    // });
    const inWebView = webViewState.inWebView;
    const webViewLoaded = webViewState.webViewLoaded;
    const listeners = useRef({});

    const [devTools, setDevTools] = useLocalStorage('devTools', false);

    const addListener = useCallback((...args) => {
        _addListener(listeners.current, ...args)
    }, []);
    const removeListener = useCallback((...args) => {
        _removeListener(listeners.current, ...args)
    }, []);
    const notify = useCallback((...args) => {
        _notify(listeners.current, ...args)
    }, []);

    useEffect(() => {
        window.WebViewManager = {
            addListener: (...args) => {
                _addListener(listeners.current, ...args)
            },
            removeListener: (...args) => {
                _removeListener(listeners.current, ...args)
            },
            notify: (...args) => {
                _notify(listeners.current, ...args)
            },
            setWebViewState: (arg) => {
                setWebViewState(arg)
            }
        };
    }, []);

    // init
    useEffect(() => {
        if (webViewLoaded) {
            console.log("set-inspectable", devTools, "| webViewLoaded:", webViewLoaded);
            notify("set-inspectable", devTools);
        }
    }, [webViewLoaded, notify, devTools]);

    return (
        <WebViewManagerContext.Provider value={
            {
                webViewState, inWebView,
                addListener, removeListener, notify,
                devTools, setDevTools,
            }}>
            {props.children}
        </WebViewManagerContext.Provider>
    )
}

// // mock
// (function () {
//     if (window.WebViewManagerMock !== undefined)
//         return;
//     window.WebViewManagerMock = true;

//     const checkInterval = 1000;
//     const timer = setInterval(() => {
//         if (window.WebViewManager) {
//             if (window.WebViewManager.setWebViewState) {
//                 clearInterval(timer); // Stops the loop immediately
//                 window.WebViewManager.setWebViewState({
//                     webViewLoaded: true,
//                     inWebView: true,
//                     supportUDP: true,
//                     supportESPTouchV1: true,
//                 });
//                 window.WebViewManager.addListener("get-wifi-info", function () {
//                     window.WebViewManager.notify("got-wifi-info", "12:34:12:34:12:34", "mock-ssid");
//                 });
//                 window.WebViewManager.addListener("broadcast-udp", function (address, port, data) {
//                     const json = JSON.parse(data);
//                     if (json["method"] === "search_for_solo_device")
//                         window.WebViewManager.notify("got-udp", "1.2.3.4", 3361, JSON.stringify({
//                             "jsonrpc": "2.0",
//                             "result": {
//                                 "serial_id": "mock-serial-" + String(Math.ceil(Math.random() * 100)),
//                                 "token": "aabbccdd",
//                                 "brand": "hinava",
//                                 "type": json["params"]["types"][(Math.random() * json["params"]["types"].length) | 0],
//                                 "model": "mock-model",
//                                 "firmware_version": 1
//                             },
//                             "id": json["id"]
//                         }));
//                 });
//                 window.WebViewManager.addListener("unicast-udp", function (address, port, data) {
//                     const json = JSON.parse(data);
//                     if (json["method"] === "join_solo")
//                         window.WebViewManager.notify("got-udp", "1.2.3.4", 3361, JSON.stringify({
//                             "jsonrpc": "2.0",
//                             "result": "ok",
//                             "id": json["id"]
//                         }));
//                 });
//             }
//         }
//     }, checkInterval);
// })();
